import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ScatterChart, Scatter, ZAxis } from 'recharts';
import { BarChart2, Users, TrendingUp, TrendingDown, Download, Filter, Calendar, RefreshCw } from 'lucide-react';
import LaborPerformance from './LaborPerformance';
import { motion } from 'framer-motion';
import { WarehousePDFGenerator } from '../../utils/warehousePDFGenerator';

const WarehouseAnalytics = () => {
  const [activeTab, setActiveTab] = useState('performance'); // performance, analytics
  const [timeRange, setTimeRange] = useState('6months'); // week, month, 3months, 6months, year
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');

  // Comprehensive mock data
  const analyticsData = [
    { month: 'Jan', throughput: 12500, accuracy: 98.5, utilization: 72, orders: 1240, returns: 45, cycleTime: 2.3, costPerOrder: 12.5 },
    { month: 'Feb', throughput: 13800, accuracy: 98.8, utilization: 75, orders: 1380, returns: 38, cycleTime: 2.1, costPerOrder: 11.8 },
    { month: 'Mar', throughput: 15200, accuracy: 99.1, utilization: 77, orders: 1520, returns: 32, cycleTime: 2.0, costPerOrder: 11.2 },
    { month: 'Apr', throughput: 16800, accuracy: 99.2, utilization: 78, orders: 1680, returns: 28, cycleTime: 1.9, costPerOrder: 10.9 },
    { month: 'May', throughput: 18200, accuracy: 99.0, utilization: 76, orders: 1820, returns: 35, cycleTime: 2.0, costPerOrder: 11.1 },
    { month: 'Jun', throughput: 19500, accuracy: 99.3, utilization: 77, orders: 1950, returns: 25, cycleTime: 1.8, costPerOrder: 10.5 },
  ];

  const dailyData = [
    { day: 'Mon', inbound: 450, outbound: 420, accuracy: 99.2, efficiency: 92 },
    { day: 'Tue', inbound: 520, outbound: 510, accuracy: 99.5, efficiency: 94 },
    { day: 'Wed', inbound: 480, outbound: 470, accuracy: 99.1, efficiency: 91 },
    { day: 'Thu', inbound: 550, outbound: 540, accuracy: 99.4, efficiency: 93 },
    { day: 'Fri', inbound: 500, outbound: 490, accuracy: 99.3, efficiency: 92 },
    { day: 'Sat', inbound: 320, outbound: 310, accuracy: 98.8, efficiency: 88 },
    { day: 'Sun', inbound: 280, outbound: 270, accuracy: 98.5, efficiency: 86 },
  ];

  const categoryPerformance = [
    { category: 'Electronics', orders: 1250, accuracy: 99.5, avgTime: 1.8, value: 1250000 },
    { category: 'Furniture', orders: 890, accuracy: 98.8, avgTime: 3.2, value: 445000 },
    { category: 'Appliances', orders: 650, accuracy: 99.2, avgTime: 2.5, value: 325000 },
    { category: 'Office Supplies', orders: 2100, accuracy: 99.8, avgTime: 1.2, value: 105000 },
    { category: 'Raw Materials', orders: 450, accuracy: 98.5, avgTime: 4.1, value: 900000 },
  ];

  const inventoryTurnover = [
    { category: 'Electronics', turnover: 8.5, daysOnHand: 43, value: 1250000 },
    { category: 'Furniture', turnover: 4.2, daysOnHand: 87, value: 445000 },
    { category: 'Appliances', turnover: 6.1, daysOnHand: 60, value: 325000 },
    { category: 'Office Supplies', turnover: 12.3, daysOnHand: 30, value: 105000 },
    { category: 'Raw Materials', turnover: 3.8, daysOnHand: 96, value: 900000 },
  ];

  const zoneUtilization = [
    { zone: 'Zone A', utilization: 85, capacity: 50000, used: 42500, efficiency: 92 },
    { zone: 'Zone B', utilization: 72, capacity: 35000, used: 25200, efficiency: 88 },
    { zone: 'Zone C', utilization: 78, capacity: 40000, used: 31200, efficiency: 90 },
    { zone: 'Zone D', utilization: 91, capacity: 45000, used: 40950, efficiency: 95 },
    { zone: 'Zone E', utilization: 65, capacity: 30000, used: 19500, efficiency: 85 },
  ];

  const orderFulfillment = [
    { status: 'On-Time', count: 1850, percentage: 94.8 },
    { status: 'Early', count: 85, percentage: 4.4 },
    { status: 'Late', count: 15, percentage: 0.8 },
  ];

  const pickingMethods = [
    { method: 'Wave Picking', orders: 850, avgTime: 1.8, accuracy: 99.2 },
    { method: 'Batch Picking', orders: 620, avgTime: 2.1, accuracy: 98.9 },
    { method: 'Directed Paths', orders: 480, avgTime: 1.5, accuracy: 99.5 },
  ];

  const errorAnalysis = [
    { type: 'Wrong Item', count: 12, percentage: 35 },
    { type: 'Wrong Quantity', count: 8, percentage: 24 },
    { type: 'Damaged Goods', count: 7, percentage: 21 },
    { type: 'Location Error', count: 4, percentage: 12 },
    { type: 'Other', count: 3, percentage: 8 },
  ];

  const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

  const handleExportPDF = async () => {
    try {
      const reportData = {
        reportId: `ANALYTICS-${Date.now()}`,
        period: timeRange,
        metrics: {
          throughput: 19500,
          accuracy: 99.3,
          utilization: 77,
          costPerOrder: 10.5,
          cycleTime: 1.8
        },
        workers: [] // Would include worker data if available
      };

      const blob = await WarehousePDFGenerator.generatePerformanceReport(reportData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `warehouse-analytics-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Analytics report exported successfully!');
    } catch (error) {
      console.error('Error exporting analytics:', error);
      alert('Error exporting report. Please try again.');
    }
  };

  const renderAnalytics = () => {
    return (
      <div className="space-y-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Throughput', value: '19500', unit: 'items/month', trend: '+12%', icon: TrendingUp, color: 'green' },
            { label: 'Accuracy Rate', value: '99.3%', unit: 'average', trend: '+0.5%', icon: TrendingUp, color: 'blue' },
            { label: 'Utilization', value: '77%', unit: 'capacity', trend: '+2%', icon: TrendingUp, color: 'orange' },
            { label: 'Cost per Order', value: 'SAR 10.5', unit: 'average', trend: '-8%', icon: TrendingDown, color: 'green' },
          ].map((metric, index) => {
            const Icon = metric.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                    <Icon className={`w-6 h-6 text-${metric.color}-600`} />
                  </div>
                  <span className={`text-sm font-medium ${
                    metric.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.trend}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{metric.unit}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Throughput & Accuracy Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Throughput Trend</h3>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analyticsData}>
                <defs>
                  <linearGradient id="throughputGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="throughput" stroke="#f59e0b" fillOpacity={1} fill="url(#throughputGradient)" name="Throughput" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Accuracy & Utilization</h3>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="utilization" fill="#3b82f6" name="Utilization %" />
                <Line yAxisId="right" type="monotone" dataKey="accuracy" stroke="#10b981" strokeWidth={2} name="Accuracy %" />
              </ComposedChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Daily Performance & Order Fulfillment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="inbound" fill="#10b981" name="Inbound" />
                <Bar yAxisId="left" dataKey="outbound" fill="#f59e0b" name="Outbound" />
                <Line yAxisId="right" type="monotone" dataKey="accuracy" stroke="#3b82f6" strokeWidth={2} name="Accuracy %" />
                <Line yAxisId="right" type="monotone" dataKey="efficiency" stroke="#8b5cf6" strokeWidth={2} name="Efficiency %" />
              </ComposedChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Fulfillment Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={orderFulfillment}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {orderFulfillment.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {orderFulfillment.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-lg font-bold text-gray-900">{item.count}</div>
                  <div className="text-xs text-gray-600">{item.status}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Category Performance & Inventory Turnover */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
                <Bar dataKey="accuracy" fill="#10b981" name="Accuracy %" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Turnover Rate</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={inventoryTurnover}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="turnover" fill="#8b5cf6" name="Turnover Rate" />
                <Line yAxisId="right" type="monotone" dataKey="daysOnHand" stroke="#ef4444" strokeWidth={2} name="Days on Hand" />
              </ComposedChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Zone Utilization & Picking Methods */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Zone Utilization</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={zoneUtilization}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="zone" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="utilization" fill="#f59e0b" name="Utilization %" />
                <Bar dataKey="efficiency" fill="#10b981" name="Efficiency %" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Picking Methods Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pickingMethods}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="method" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
                <Bar dataKey="avgTime" fill="#f59e0b" name="Avg Time (hrs)" />
                <Bar dataKey="accuracy" fill="#10b981" name="Accuracy %" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Error Analysis & Cost Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Error Analysis</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={errorAnalysis}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {errorAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="costPerOrder" stroke="#ef4444" strokeWidth={2} name="Cost per Order (SAR)" />
                <Line type="monotone" dataKey="cycleTime" stroke="#8b5cf6" strokeWidth={2} name="Cycle Time (days)" />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Returns & Orders Correlation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders vs Returns Correlation</h3>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart data={analyticsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="orders" name="Orders" />
              <YAxis type="number" dataKey="returns" name="Returns" />
              <ZAxis type="number" dataKey="accuracy" range={[50, 400]} name="Accuracy" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="Performance" data={analyticsData} fill="#f59e0b" />
            </ScatterChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Warehouse Analytics</h2>
            <p className="text-sm text-gray-600 mt-1">Comprehensive performance metrics and insights</p>
          </div>
          <div className="flex gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="year">Last Year</option>
            </select>
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Warehouses</option>
              <option value="1">Main Warehouse</option>
              <option value="2">Distribution Center A</option>
              <option value="3">Cold Storage</option>
              <option value="4">Express Hub</option>
            </select>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'performance', label: 'Labor & Performance', icon: Users },
            { id: 'analytics', label: 'Warehouse Analytics', icon: BarChart2 }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-orange-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'performance' && <LaborPerformance />}
      {activeTab === 'analytics' && renderAnalytics()}
    </div>
  );
};

export default WarehouseAnalytics;
